import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.tsx';
import { Flame, Mail, Lock, User, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function RegisterPage() {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [role, setRole] = useState<'USER' | 'MANAGER'>('USER');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    // Password strength indicator
    const getPasswordStrength = (pwd: string) => {
        if (pwd.length === 0) return { strength: 0, label: '' };
        if (pwd.length < 8) return { strength: 1, label: 'Weak', color: 'bg-red-500' };

        let strength = 1;
        if (/[A-Z]/.test(pwd)) strength++;
        if (/[a-z]/.test(pwd)) strength++;
        if (/[0-9]/.test(pwd)) strength++;
        if (/[^A-Za-z0-9]/.test(pwd)) strength++;

        if (strength <= 2) return { strength: 2, label: 'Fair', color: 'bg-alert-500' };
        if (strength === 3) return { strength: 3, label: 'Good', color: 'bg-safety-400' };
        return { strength: 4, label: 'Strong', color: 'bg-safety-600' };
    };

    const passwordStrength = getPasswordStrength(password);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setLoading(true);

        try {
            await register(email, password, fullName, role);
            navigate('/dashboard');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Registration failed');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-steel-50 via-steel-100 to-steel-200 p-4 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-0 right-0 w-96 h-96 bg-ember-500 rounded-full blur-3xl animate-float" />
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-safety-500 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
            </div>

            <div className="w-full max-w-md relative z-10 animate-fadeIn">
                {/* Logo \u0026 Brand */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-ember-500 to-ember-600 rounded-2xl shadow-ember mb-4 animate-float">
                        <Flame className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-display-2 font-bold text-charcoal-950 mb-2">
                        Create Account
                    </h1>
                    <p className="text-steel-600">
                        Join the modern compliance platform
                    </p>
                </div>

                {/* Register Card */}
                <div className="card-glass p-8 animate-slideUp">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Error Alert */}
                        {error && (
                            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl animate-scaleIn">
                                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        )}

                        {/* Full Name */}
                        <div>
                            <label htmlFor="fullName" className="label">
                                Full Name
                            </label>
                            <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                                <input
                                    id="fullName"
                                    type="text"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    className="input pl-12"
                                    placeholder="John Doe"
                                    required
                                    autoComplete="name"
                                />
                            </div>
                        </div>

                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="label">
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input pl-12"
                                    placeholder="you@company.com"
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        {/* Role Selection */}
                        <div>
                            <label className="label">Account Type</label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setRole('USER')}
                                    className={`p-4 rounded-xl border-2 transition-all ${role === 'USER'
                                            ? 'border-ember-500 bg-ember-50'
                                            : 'border-steel-200 bg-white hover:border-steel-300'
                                        }`}
                                >
                                    <div className="text-sm font-medium text-charcoal-900">User</div>
                                    <div className="text-xs text-steel-600 mt-1">View \u0026 Report</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRole('MANAGER')}
                                    className={`p-4 rounded-xl border-2 transition-all ${role === 'MANAGER'
                                            ? 'border-ember-500 bg-ember-50'
                                            : 'border-steel-200 bg-white hover:border-steel-300'
                                        }`}
                                >
                                    <div className="text-sm font-medium text-charcoal-900">Manager</div>
                                    <div className="text-xs text-steel-600 mt-1">Full Access</div>
                                </button>
                            </div>
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="label">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                                <input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input pl-12"
                                    placeholder="••••••••"
                                    required
                                    autoComplete="new-password"
                                />
                            </div>
                            {/* Password Strength */}
                            {password && (
                                <div className="mt-2 space-y-1">
                                    <div className="flex gap-1">
                                        {[1, 2, 3, 4].map((level) => (
                                            <div
                                                key={level}
                                                className={`h-1 flex-1 rounded-full transition-all ${level <= passwordStrength.strength
                                                        ? passwordStrength.color
                                                        : 'bg-steel-200'
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                    <p className="text-xs text-steel-600">
                                        Strength: <span className="font-medium">{passwordStrength.label}</span>
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirmPassword" className="label">
                                Confirm Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                                <input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="input pl-12"
                                    placeholder="••••••••"
                                    required
                                    autoComplete="new-password"
                                />
                                {confirmPassword && password === confirmPassword && (
                                    <CheckCircle2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-safety-500" />
                                )}
                            </div>
                        </div>

                        {/* Terms */}
                        <label className="flex items-start gap-3 cursor-pointer group">
                            <input
                                type="checkbox"
                                required
                                className="mt-0.5 w-4 h-4 rounded border-steel-300 text-ember-600 focus:ring-2 focus:ring-ember-500 focus:ring-offset-0 transition-all"
                            />
                            <span className="text-sm text-steel-700 group-hover:text-steel-900 transition-colors">
                                I agree to the{' '}
                                <a href="/terms" className="text-ember-600 hover:text-ember-700 font-medium">
                                    Terms of Service
                                </a>{' '}
                                and{' '}
                                <a href="/privacy" className="text-ember-600 hover:text-ember-700 font-medium">
                                    Privacy Policy
                                </a>
                            </span>
                        </label>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn btn-primary w-full group relative overflow-hidden"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center gap-2">
                                    <div className="spinner" />
                                    <span>Creating account...</span>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center gap-2">
                                    <span>Create Account</span>
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </div>
                            )}
                        </button>
                    </form>

                    {/* Login Link */}
                    <div className="mt-6 text-center text-sm text-steel-600">
                        Already have an account?{' '}
                        <a
                            href="/login"
                            className="text-ember-600 hover:text-ember-700 font-medium transition-colors"
                        >
                            Sign in
                        </a>
                    </div>
                </div>

                {/* Footer */}
                <p className="text-center text-sm text-steel-500 mt-8">
                    © 2025 Fire Safety Compliance. Built with care for safety professionals.
                </p>
            </div>
        </div>
    );
}
